// Action Types
export const FETCH_EQUIPMENT_CONFIGS_REQUEST =
  "FETCH_EQUIPMENT_CONFIGS_REQUEST";
export const FETCH_EQUIPMENT_CONFIGS_SUCCESS =
  "FETCH_EQUIPMENT_CONFIGS_SUCCESS";
export const FETCH_EQUIPMENT_CONFIGS_FAILURE =
  "FETCH_EQUIPMENT_CONFIGS_FAILURE";

export const CREATE_EQUIPMENT_CONFIG_REQUEST =
  "CREATE_EQUIPMENT_CONFIG_REQUEST";
export const CREATE_EQUIPMENT_CONFIG_SUCCESS =
  "CREATE_EQUIPMENT_CONFIG_SUCCESS";
export const CREATE_EQUIPMENT_CONFIG_FAILURE =
  "CREATE_EQUIPMENT_CONFIG_FAILURE";

export const UPDATE_EQUIPMENT_CONFIG_REQUEST =
  "UPDATE_EQUIPMENT_CONFIG_REQUEST";
export const UPDATE_EQUIPMENT_CONFIG_SUCCESS =
  "UPDATE_EQUIPMENT_CONFIG_SUCCESS";
export const UPDATE_EQUIPMENT_CONFIG_FAILURE =
  "UPDATE_EQUIPMENT_CONFIG_FAILURE";

export const DELETE_EQUIPMENT_CONFIG_REQUEST =
  "DELETE_EQUIPMENT_CONFIG_REQUEST";
export const DELETE_EQUIPMENT_CONFIG_SUCCESS =
  "DELETE_EQUIPMENT_CONFIG_SUCCESS";
export const DELETE_EQUIPMENT_CONFIG_FAILURE =
  "DELETE_EQUIPMENT_CONFIG_FAILURE";

export const SET_EQUIPMENT_CONFIG_FILTERS = "SET_EQUIPMENT_CONFIG_FILTERS";
export const RESET_EQUIPMENT_CONFIG_FILTERS = "RESET_EQUIPMENT_CONFIG_FILTERS";

// Action Creators
export const fetchEquipmentConfigsRequest = (filters = {}) => ({
  type: FETCH_EQUIPMENT_CONFIGS_REQUEST,
  payload: { filters },
});

export const fetchEquipmentConfigsSuccess = (data) => ({
  type: FETCH_EQUIPMENT_CONFIGS_SUCCESS,
  payload: data,
});

export const fetchEquipmentConfigsFailure = (error) => ({
  type: FETCH_EQUIPMENT_CONFIGS_FAILURE,
  payload: error,
});

export const createEquipmentConfigRequest = (data) => ({
  type: CREATE_EQUIPMENT_CONFIG_REQUEST,
  payload: { data },
});

export const createEquipmentConfigSuccess = (data) => ({
  type: CREATE_EQUIPMENT_CONFIG_SUCCESS,
  payload: data,
});

export const createEquipmentConfigFailure = (error) => ({
  type: CREATE_EQUIPMENT_CONFIG_FAILURE,
  payload: error,
});

export const updateEquipmentConfigRequest = (id, data) => ({
  type: UPDATE_EQUIPMENT_CONFIG_REQUEST,
  payload: { id, data },
});

export const updateEquipmentConfigSuccess = (data) => ({
  type: UPDATE_EQUIPMENT_CONFIG_SUCCESS,
  payload: data,
});

export const updateEquipmentConfigFailure = (error) => ({
  type: UPDATE_EQUIPMENT_CONFIG_FAILURE,
  payload: error,
});

export const deleteEquipmentConfigRequest = (id) => ({
  type: DELETE_EQUIPMENT_CONFIG_REQUEST,
  payload: { id },
});

export const deleteEquipmentConfigSuccess = (data) => ({
  type: DELETE_EQUIPMENT_CONFIG_SUCCESS,
  payload: data,
});

export const deleteEquipmentConfigFailure = (error) => ({
  type: DELETE_EQUIPMENT_CONFIG_FAILURE,
  payload: error,
});

export const setEquipmentConfigFilters = (filters) => ({
  type: SET_EQUIPMENT_CONFIG_FILTERS,
  payload: filters,
});

export const resetEquipmentConfigFilters = () => ({
  type: RESET_EQUIPMENT_CONFIG_FILTERS,
});

const normalizePayload = (payload) => payload?.response || payload || {};

const getConfigsFromPayload = (payload) => {
  const data = normalizePayload(payload);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.configs)) return data.configs;
  if (Array.isArray(data.equipment_configs)) return data.equipment_configs;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const getConfigFromPayload = (payload) => {
  const data = normalizePayload(payload);
  if (!data) return null;
  return (
    data.config ||
    data.equipment_config ||
    data.data ||
    data.item ||
    (data.id ? data : null)
  );
};

const matchesFilters = (config, filters) => {
  if (!config) return false;
  if (filters.sport && config.sport !== filters.sport) return false;
  if (filters.location && config.location !== filters.location) return false;
  if (!filters.include_inactive && config.is_active === false) return false;
  return true;
};

// Initial State
const initialState = {
  configs: [],
  total: 0,
  loading: {
    list: false,
    create: false,
    update: false,
    delete: false,
  },
  error: {
    list: null,
    create: null,
    update: null,
    delete: null,
  },
  filters: {
    sport: "",
    location: "",
    include_inactive: false,
  },
};

// Reducer
const equipmentConfigReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_EQUIPMENT_CONFIGS_REQUEST:
      return {
        ...state,
        loading: { ...state.loading, list: true },
        error: { ...state.error, list: null },
      };

    case FETCH_EQUIPMENT_CONFIGS_SUCCESS: {
      const configs = getConfigsFromPayload(action.payload);
      const data = normalizePayload(action.payload);
      const total = data.total || data.count || configs.length || 0;
      return {
        ...state,
        configs,
        total,
        loading: { ...state.loading, list: false },
      };
    }

    case FETCH_EQUIPMENT_CONFIGS_FAILURE:
      return {
        ...state,
        loading: { ...state.loading, list: false },
        error: { ...state.error, list: action.payload },
      };

    case CREATE_EQUIPMENT_CONFIG_REQUEST:
      return {
        ...state,
        loading: { ...state.loading, create: true },
        error: { ...state.error, create: null },
      };

    case CREATE_EQUIPMENT_CONFIG_SUCCESS: {
      const created = getConfigFromPayload(action.payload);
      if (!created) {
        return {
          ...state,
          loading: { ...state.loading, create: false },
        };
      }
      if (!matchesFilters(created, state.filters)) {
        return {
          ...state,
          loading: { ...state.loading, create: false },
        };
      }
      return {
        ...state,
        configs: [created, ...state.configs],
        total: state.total + 1,
        loading: { ...state.loading, create: false },
      };
    }

    case CREATE_EQUIPMENT_CONFIG_FAILURE:
      return {
        ...state,
        loading: { ...state.loading, create: false },
        error: { ...state.error, create: action.payload },
      };

    case UPDATE_EQUIPMENT_CONFIG_REQUEST:
      return {
        ...state,
        loading: { ...state.loading, update: true },
        error: { ...state.error, update: null },
      };

    case UPDATE_EQUIPMENT_CONFIG_SUCCESS: {
      const updated = getConfigFromPayload(action.payload);
      if (!updated) {
        return {
          ...state,
          loading: { ...state.loading, update: false },
        };
      }
      const stillMatches = matchesFilters(updated, state.filters);
      const exists = state.configs.some((cfg) => cfg.id === updated.id);
      return {
        ...state,
        configs: stillMatches
          ? exists
            ? state.configs.map((cfg) =>
                cfg.id === updated.id ? { ...cfg, ...updated } : cfg
              )
            : [updated, ...state.configs]
          : state.configs.filter((cfg) => cfg.id !== updated.id),
        loading: { ...state.loading, update: false },
      };
    }

    case UPDATE_EQUIPMENT_CONFIG_FAILURE:
      return {
        ...state,
        loading: { ...state.loading, update: false },
        error: { ...state.error, update: action.payload },
      };

    case DELETE_EQUIPMENT_CONFIG_REQUEST:
      return {
        ...state,
        loading: { ...state.loading, delete: true },
        error: { ...state.error, delete: null },
      };

    case DELETE_EQUIPMENT_CONFIG_SUCCESS: {
      const updated = getConfigFromPayload(action.payload);
      if (!updated) {
        return {
          ...state,
          loading: { ...state.loading, delete: false },
        };
      }
      const stillMatches = matchesFilters(updated, state.filters);
      return {
        ...state,
        configs: stillMatches
          ? state.configs.map((cfg) =>
              cfg.id === updated.id ? { ...cfg, ...updated } : cfg
            )
          : state.configs.filter((cfg) => cfg.id !== updated.id),
        loading: { ...state.loading, delete: false },
      };
    }

    case DELETE_EQUIPMENT_CONFIG_FAILURE:
      return {
        ...state,
        loading: { ...state.loading, delete: false },
        error: { ...state.error, delete: action.payload },
      };

    case SET_EQUIPMENT_CONFIG_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case RESET_EQUIPMENT_CONFIG_FILTERS:
      return {
        ...state,
        filters: initialState.filters,
      };

    default:
      return state;
  }
};

export default equipmentConfigReducer;
